import {
  Database,
  DollarSign,
  MessageSquare,
  ShoppingCart,
  Truck,
  Users,
} from "lucide-react";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../components/ui/card";
import { cn } from "../../../../../lib/utils";
import { IndriveCheckoutItem } from "../types";

interface IndriveCheckoutItemCardProps {
  checkout: IndriveCheckoutItem;
  onConfirmCheckout: (filterDate: string, ops: string) => void;
  onStartDoc: (filterDate: string, ops: string) => void;
  onSubmitRemessa: (filterDate: string, ops: string) => void;
  isConfirming: boolean;
  isStartingDoc: boolean;
  isSubmittingRemessa: boolean;
  hasNewerDate: boolean;
}

export function IndriveCheckoutItemCard({
  checkout,
  onConfirmCheckout,
  onStartDoc,
  onSubmitRemessa,
  isConfirming,
  isStartingDoc,
  isSubmittingRemessa,
  hasNewerDate,
}: IndriveCheckoutItemCardProps) {
  const dateToCheckout = checkout.date_to_checkout.slice(0, 10);
  const wtsDisabled =
    checkout.status === "closed" ||
    checkout.status === "committed" ||
    hasNewerDate;
  const docDisabled =
    checkout.status === "requested" || checkout.status === "committed";
  // "Inserir no DB" deve ficar habilitado em "closed" (logo após "Realizar
  // compra"), que é o status imediatamente anterior a "committed". Sem isso o
  // fluxo trava em "closed" e nunca chega a "committed".
  const insertDbDisabled =
    checkout.status === "requested" ||
    checkout.status === "confirmed" ||
    checkout.status === "committed";

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      { label: string; variant: "default" | "secondary" | "destructive" }
    > = {
      pending: { label: "Pendente", variant: "default" },
      requested: { label: "Solicitado", variant: "secondary" },
      confirmed: { label: "Confirmado", variant: "secondary" },
      closed: { label: "Fechado", variant: "destructive" },
      payment_requested: { label: "Pagamento Solicitado", variant: "default" },
      committed: { label: "Comprado", variant: "secondary" },
    };

    const statusInfo = statusMap[status] || {
      label: status,
      variant: "default" as const,
    };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">
            {dateToCheckout}
          </CardTitle>
          {getStatusBadge(checkout.status)}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/10">
            <div className="p-2 rounded-full bg-secondary/20">
              <Truck className="h-5 w-5 text-secondary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Operação</p>
              <p className="font-semibold text-foreground">{checkout.ops}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/10">
            <div className="p-2 rounded-full bg-blue-500/20">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">MB únicos</p>
              <p className="font-semibold text-foreground">
                {checkout.entregadores_unicos_cadastrados ??
                  checkout.entregadores_unicos ??
                  0}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10">
            <div className="p-2 rounded-full bg-green-500/20">
              <Truck className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ticket Médio</p>
              <p className="font-semibold text-foreground">
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(checkout.ticket_medio / 100)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-500/10">
            <div className="p-2 rounded-full bg-purple-500/20">
              <DollarSign className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Valor Acumulado</p>
              <p className="font-semibold text-foreground">
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(checkout.total_valor_face / 100)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onConfirmCheckout(dateToCheckout, checkout.ops)}
            disabled={wtsDisabled || isConfirming}
            className={cn(
              "gap-2",
              wtsDisabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <MessageSquare className="h-4 w-4" />
            {isConfirming ? "Enviando..." : "Mandar mensagens"}
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onStartDoc(dateToCheckout, checkout.ops)}
            disabled={docDisabled || isStartingDoc}
            className={cn(
              "gap-2",
              docDisabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <ShoppingCart className="h-4 w-4" />
            {isStartingDoc ? "Processando..." : "Realizar compra"}
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onSubmitRemessa(dateToCheckout, checkout.ops)}
            disabled={insertDbDisabled || isSubmittingRemessa}
            className={cn(
              "gap-2",
              insertDbDisabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <Database className="h-4 w-4" />
            {isSubmittingRemessa ? "Inserindo..." : "Inserir no DB"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
